import {API_URL, apiGet} from '../api'
import {ApiRes} from '../../types/api'

/**
 * Volume League (manager-only) admin model.
 * 신규 통합 모델: kind(rolling | competition) + scope_type(exchange | symbol).
 * - competition  ≈ 기존 campaign (symbol scope, prize_pool, start/end)
 * - rolling      ≈ 기존 project  (exchange scope, window_days)
 * 기존 tradermap_* API 는 건드리지 않고 본 파일을 신규로 추가.
 */
export type LeagueKind = 'rolling' | 'competition'
export type LeagueScopeType = 'exchange' | 'symbol' | 'markets'

// 공개 범위 — public: 모두에게 노출 / managers: 관리자만 (테스트·준비용). 빈 값 = public.
export type LeagueVisibility = 'public' | 'managers'

// markets scope 의 집계 시장 1개. 서버가 지원하는 (거래소, 종류) 조합만 허용된다.
//  - spot:    symbol = 기초 코인, quote 필수 (예: bithumb spot BILL / KRW)
//  - futures: symbol = 계약명, quote 비움   (예: binance futures BILLUSDT)
export interface IVolumeLeagueMarket {
    exchange: string
    type: 'spot' | 'futures'
    symbol: string
    quote?: string
}
export type LeagueStatus = 'draft' | 'active' | 'paused' | 'ended'

export interface ILeaguePrizePool {
    total_amount: number
    currency: string
}

// Slot 식별자 (언어별, 표시 전용 · 선택). 값 없으면 응답에서 생략될 수 있음.
export interface ILeagueSlotId {
    ko?: string
    en?: string
}

export interface IVolumeLeague {
    id: number
    name: string
    kind: LeagueKind
    scope_type: LeagueScopeType
    status: LeagueStatus
    visibility?: LeagueVisibility | '' // 공개 범위 (빈 값 = public)
    markets?: IVolumeLeagueMarket[] | null // markets scope 일 때 집계 시장 목록
    group_label?: string // 그룹핑 기준 라벨 (없으면 name 으로 폴백)
    exchange?: string // exchange scope 일 때
    symbol?: string // symbol scope 일 때
    supported_exchanges?: string[] // 빈 배열/누락 = 모든 거래소
    exchange_urls?: {[exchange: string]: string} // 거래소명 → URL 맵 (표시 전용, 키는 supported_exchanges 와 매칭)
    window_days?: number // rolling
    refresh_interval_sec?: number
    prize_pool?: ILeaguePrizePool // competition 전용
    start_at?: string // competition
    end_at?: string // competition
    url_third_party?: string // 표시 전용 외부 링크 (값 없으면 응답에서 생략됨)
    sort_value?: number // 표시 정렬 가중치 (내림차순 정렬, 기본 0, 랭킹에는 영향 없음)
    slot_id?: ILeagueSlotId // 언어별 PC Slot 식별자 (표시 전용 · 선택)
    mobile_slot_id?: ILeagueSlotId // 언어별 모바일 Slot 식별자 (표시 전용 · 선택)
    created_at?: string
    updated_at?: string
}

// GET /v3/admin/volume-leagues — 모든 상태의 리그 목록
export const getVolumeLeagues = async (): Promise<ApiRes<IVolumeLeague[]>> => {
    return await apiGet(API_URL, `admin/volume-leagues`)
}
